import React, { useEffect, useState } from 'react';
import { ChevronUp } from 'lucide-react';

export const BackToTop: React.FC = () => {
  const [scrollProgress, setScrollProgress] = useState<number>(0);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [isHovered, setIsHovered] = useState<boolean>(false);

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollTop = window.scrollY || document.documentElement.scrollTop;
          const scrollHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;

          if (scrollHeight > 0) {
            const progress = (scrollTop / scrollHeight) * 100;
            setScrollProgress(Math.min(100, Math.max(0, progress)));
          }

          // Show button once user scrolls past 200px
          setIsVisible(scrollTop > 200);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    // Initial check
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  // SVG circular geometry parameters
  const radius = 21;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (scrollProgress / 100) * circumference;

  return (
    <div
      className={`fixed bottom-6 right-6 z-40 select-none transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${
        isVisible
          ? 'opacity-100 translate-y-0 scale-100 pointer-events-auto'
          : 'opacity-0 translate-y-6 scale-90 pointer-events-none'
      }`}
    >
      <div className="relative group">
        {/* Floating Tooltip with Dynamic Progress Percentage (Desktop hover only) */}
        <div
          className={`hidden md:flex absolute right-full mr-3.5 top-1/2 -translate-y-1/2 pointer-events-none transition-all duration-300 whitespace-nowrap bg-[#061d22]/95 backdrop-blur-md text-white text-[11px] font-mono px-2.5 py-1 rounded-full border border-[#38c9de]/40 shadow-[0_8px_20px_rgba(0,0,0,0.4)] items-center gap-2 ${
            isHovered
              ? 'opacity-100 translate-x-0'
              : 'opacity-0 translate-x-2'
          }`}
          style={{ borderRadius: '9999px' }}
        >
          <span className="text-[#38c9de] font-semibold">TOP</span>
          <span className="text-white/40">•</span>
          <span className="text-[#6ee7b7] font-medium">{Math.round(scrollProgress)}%</span>
        </div>

        {/* Pure Circular Progress Button */}
        <button
          type="button"
          onClick={scrollToTop}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          aria-label={`Scroll back to top, page is ${Math.round(scrollProgress)}% scrolled`}
          className="relative w-12 h-12 sm:w-13 sm:h-13 rounded-full flex items-center justify-center bg-[#061d22]/90 backdrop-blur-xl border border-[#38c9de]/30 text-[#38c9de] hover:text-white shadow-[0_8px_25px_rgba(0,0,0,0.45)] hover:shadow-[0_0_24px_rgba(56,201,222,0.45)] hover:border-[#38c9de] transition-all duration-300 active:scale-90 cursor-pointer group overflow-hidden"
          style={{ borderRadius: '9999px' }}
        >
          {/* SVG Circular Progress Track & Fill */}
          <svg
            className="absolute inset-0 w-full h-full -rotate-90 pointer-events-none p-[2px]"
            viewBox="0 0 48 48"
          >
            {/* Ambient Background Track Circle */}
            <circle
              cx="24"
              cy="24"
              r={radius}
              fill="none"
              stroke="rgba(255, 255, 255, 0.12)"
              strokeWidth="2.2"
            />

            {/* Gradient Definition for Progressive Stroke */}
            <defs>
              <linearGradient id="backToTopGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#38c9de" />
                <stop offset="100%" stopColor="#6ee7b7" />
              </linearGradient>
            </defs>

            {/* Active Progressive Scroll Stroke */}
            <circle
              cx="24"
              cy="24"
              r={radius}
              fill="none"
              stroke="url(#backToTopGradient)"
              strokeWidth="2.4"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              strokeLinecap="round"
              className="transition-[stroke-dashoffset] duration-150 ease-out"
            />
          </svg>

          {/* Center Upward Chevron Icon with Hover Micro-Motion */}
          <div className="relative z-10 flex items-center justify-center transition-transform duration-300 group-hover:-translate-y-0.5">
            <ChevronUp className="w-5 h-5 sm:w-5.5 sm:h-5.5 stroke-[2.4] text-[#38c9de] group-hover:text-white transition-colors duration-300 drop-shadow-[0_0_8px_rgba(56,201,222,0.5)]" />
          </div>

          {/* Subtle Ambient Pulse Flare on full 100% scroll depth */}
          {scrollProgress > 95 && (
            <span className="absolute inset-0 rounded-full bg-[#38c9de]/15 animate-ping pointer-events-none" />
          )}
        </button>
      </div>
    </div>
  );
};
